/* YouTube カルーセル（sections/tds-youtube.liquid）
   - メインのカード（ショートは数枚）の左右に前後のカードを見切れさせ、左右ボタン・下の点・スワイプ・トラックパッドの横スクロール・
     キーボードの左右キーで送る。端まで行くと反対の端へつながる（ループ）。見切れたカードを押してもそちらへ送る
   - サムネイルを押すとその場で再生する（同時に鳴らさない。送ったら止める）
   - 計測（2026-09-30）：GTM の dataLayer へ yt_carousel_* を送る。GA4 への送り先は GTM で設定する。
     押せる要素にも同じ内容の data-ga-* が付いている */
(() => {
  // セクションごとに同じスクリプトを読み込むので、2回目以降は何もしない（クリックの二重計測・二重再生を防ぐ）
  if (window.tdsYoutubeCarousel) return;
  window.tdsYoutubeCarousel = true;
  const VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
  const PROGRESS = [25, 50, 75];
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- 計測 ----------
  // GTM のデータレイヤーは、前に送った値をキーごとに覚えている。そのイベントに無いキーは undefined で消してから送る
  // （消さないと、再生のあとの「送り」に前の動画の題名や % が付いてしまう）
  const KEYS = ['carousel_type', 'video_id', 'video_title', 'video_url', 'video_position', 'video_provider', 'video_percent',
    'video_duration', 'play_mode', 'link_url', 'nav_method', 'nav_to_position'];
  const track = (event, element, extra = {}) => {
    if (window.Shopify?.designMode) return;
    const data = element?.dataset || {};
    const type = data.gaCarousel || element?.closest?.('[data-tyt]')?.dataset.gaCarousel || '';
    // 計測の種類（data-ga-carousel）を持たないカルーセル（記事のカルーセル）は、yt_carousel_* を送らない
    if (!type) return;
    const detail = Object.fromEntries(KEYS.map((key) => [key, undefined]));
    Object.assign(detail, { event, carousel_type: type });
    if (data.gaVideoId) {
      Object.assign(detail, {
        video_id: data.gaVideoId, video_title: data.gaVideoTitle || '', video_url: data.gaVideoUrl || '',
        video_position: Number(data.gaPosition) || 0, video_provider: 'youtube',
      });
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ...detail, ...extra });
  };

  // 再生の開始・25/50/75%・最後まで、を YouTube の IFrame API で拾う（最初に再生したときだけ読み込む）
  let api;
  const loadApi = () => {
    api = api || new Promise((resolve, reject) => {
      if (window.YT?.Player) { resolve(window.YT); return; }
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof previous === 'function') previous();
        resolve(window.YT);
      };
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      script.onerror = () => reject(new Error('iframe_api'));
      document.head.appendChild(script);
    });
    return api;
  };

  const watch = (frame, link) => {
    loadApi().then((YT) => {
      if (!frame.isConnected) return;
      const sent = new Set();
      let timer = 0;
      let player = null;
      const stopTimer = () => { clearInterval(timer); timer = 0; };
      const seconds = (name) => { try { return Number(player[name]()) || 0; } catch (error) { return 0; } };
      const check = (upTo = 100) => {
        const duration = seconds('getDuration');
        if (!duration) return;
        const percent = Math.min(upTo, (seconds('getCurrentTime') / duration) * 100);
        PROGRESS.filter((mark) => percent >= mark && !sent.has(mark)).forEach((mark) => {
          sent.add(mark);
          track('yt_carousel_video_progress', link, { video_percent: mark, video_duration: Math.round(duration) });
        });
      };
      frame.tytStop = stopTimer;
      player = new YT.Player(frame, {
        events: {
          onStateChange: ({ data }) => {
            if (data === YT.PlayerState.PLAYING) {
              if (!sent.has('start')) {
                sent.add('start');
                track('yt_carousel_video_start', link, { video_duration: Math.round(seconds('getDuration')) });
              }
              if (!timer) timer = setInterval(() => (frame.isConnected ? check() : stopTimer()), 1000);
              return;
            }
            stopTimer();
            if (data === YT.PlayerState.ENDED && !sent.has('complete')) {
              sent.add('complete');
              check();
              track('yt_carousel_video_complete', link, { video_percent: 100, video_duration: Math.round(seconds('getDuration')) });
            }
          },
        },
      });
    }).catch(() => {});
  };

  // ---------- その場で再生 ----------
  const stopAll = () => {
    document.querySelectorAll('.tyt-media.is-playing').forEach((media) => {
      const frame = media.querySelector('iframe');
      frame?.tytStop?.();
      frame?.remove();
      const link = media.querySelector('[data-tyt-play]');
      if (link) link.hidden = false;
      media.classList.remove('is-playing');
    });
  };

  const play = (link) => {
    const id = link.dataset.id || '';
    if (!VIDEO_ID.test(id)) return false;
    stopAll();
    const media = link.parentElement;
    const frame = document.createElement('iframe');
    const params = new URLSearchParams({ autoplay: '1', playsinline: '1', rel: '0', enablejsapi: '1', origin: location.origin });
    frame.src = `https://www.youtube-nocookie.com/embed/${id}?${params}`;
    frame.title = link.dataset.title || 'YouTube 動画';
    frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    frame.allowFullscreen = true;
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    link.hidden = true;
    media.appendChild(frame);
    media.classList.add('is-playing');
    frame.focus();
    track('yt_carousel_play', link, { play_mode: 'inline' });
    watch(frame, link);
    return true;
  };

  // ---------- 送り ----------
  const init = (root) => {
    if (root.dataset.tytReady) return;
    const viewport = root.querySelector('[data-tyt-viewport]');
    const strip = root.querySelector('[data-tyt-track]');
    const prev = root.querySelector('[data-tyt-prev]');
    const next = root.querySelector('[data-tyt-next]');
    const dotsBox = root.querySelector('[data-tyt-dots]');
    const status = root.querySelector('[data-tyt-status]');
    const originals = strip ? [...strip.querySelectorAll(':scope > [data-tyt-item]')] : [];
    const count = originals.length;
    if (!viewport || !count) return;
    root.dataset.tytReady = 'true';

    let visible = 1;   // 全部見える枚数（CSS の --tyt-visible）
    let clones = 0;    // 左右に足した写しの枚数
    let index = 0;     // 全部見える先頭のカード（写しの分は含まない）
    let stride = 0;    // カード1枚＋間隔
    let moving = false;
    let settleTimer = 0;
    let dots = [];
    const unit = root.dataset.tytUnit || '本'; // 数え方（動画は「本」、記事は「件」）
    const looping = () => root.classList.contains('is-carousel');
    const wrap = (value) => ((value % count) + count) % count;
    const cssVisible = () => Math.max(1, parseInt(getComputedStyle(root).getPropertyValue('--tyt-visible'), 10) || 1);

    const copy = (item) => {
      const clone = item.cloneNode(true);
      clone.classList.add('is-clone');
      clone.querySelectorAll('iframe').forEach((frame) => frame.remove());
      clone.querySelector('.tyt-media')?.classList.remove('is-playing');
      const link = clone.querySelector('[data-tyt-play]');
      if (link) link.hidden = false;
      return clone;
    };

    const place = (animate, drag = 0) => {
      root.classList.toggle('is-animating', Boolean(animate) && !reduceMotion());
      strip.style.transform = `translate3d(${-(index + clones) * stride + drag}px,0,0)`;
    };

    // 見えているカードだけを操作・読み上げの対象にする（見切れ・写しは対象外）
    const markVisible = (announce = false) => {
      [...strip.children].forEach((item, position) => {
        const at = position - clones;
        const shown = !looping() || (at >= index && at < index + visible);
        item.inert = !shown;
        if (shown) item.removeAttribute('aria-hidden'); else item.setAttribute('aria-hidden', 'true');
      });
      const current = wrap(index);
      dots.forEach((dot, i) => dot.setAttribute('aria-current', wrap(i - current) < visible ? 'true' : 'false'));
      if (status && announce && looping()) {
        const last = wrap(current + visible - 1);
        status.textContent = visible > 1 ? `${current + 1}〜${last + 1}${unit}目（全${count}${unit}）` : `${current + 1}${unit}目（全${count}${unit}）`;
      }
    };

    const settle = () => {
      clearTimeout(settleTimer);
      moving = false;
      root.classList.remove('is-animating');
      const normalized = wrap(index);
      if (normalized !== index) {
        index = normalized;
        place(false);
        void strip.offsetWidth; // 写しから本物へ瞬時に戻したことを確定させてから次の動きへ
      }
      markVisible(true);
    };

    const goTo = (target) => {
      index = target;
      markVisible(); // 点と操作の対象は動き出す時点で行き先に合わせる（写しのままでも位置で判定できる）
      if (reduceMotion()) {
        place(false);
        settle();
        return;
      }
      moving = true;
      place(true);
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 700); // transitionend が来ない場合（非表示のタブなど）の保険
    };

    const move = (delta, method, source) => {
      if (!looping()) return;
      if (moving) settle();
      stopAll();
      goTo(index + delta);
      track('yt_carousel_nav', source || root, { nav_method: method, nav_to_position: wrap(index) + 1 });
    };

    const measure = () => {
      const first = originals[0];
      const gap = parseFloat(getComputedStyle(strip).columnGap) || 0;
      stride = first.getBoundingClientRect().width + gap;
      const media = first.querySelector('.tyt-media');
      if (media) root.style.setProperty('--tyt-arrow-top', `${Math.round(media.getBoundingClientRect().height / 2)}px`);
      const offset = parseFloat(getComputedStyle(strip).paddingLeft) || 0;
      root.style.setProperty('--tyt-peek-w', `${Math.max(0, Math.floor(offset - gap))}px`);
    };

    const build = () => {
      stopAll();
      strip.querySelectorAll(':scope > .is-clone').forEach((clone) => clone.remove());
      visible = cssVisible();
      const loop = count > visible;
      root.classList.toggle('is-carousel', loop);
      prev.hidden = !loop;
      next.hidden = !loop;
      dotsBox.hidden = !loop;
      clones = loop ? Math.min(count, visible + 1) : 0;
      if (loop) {
        strip.prepend(...originals.slice(-clones).map(copy));
        strip.append(...originals.slice(0, clones).map(copy));
      }
      index = loop ? wrap(index) : 0;
      measure();
      place(false);
      markVisible();
    };

    dots = originals.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'tyt-dot';
      dot.setAttribute('aria-label', `${i + 1}${unit}目を表示`);
      dot.setAttribute('aria-controls', strip.id);
      if (root.dataset.gaCarousel) Object.assign(dot.dataset, { gaEvent: 'yt_carousel_nav', gaCarousel: root.dataset.gaCarousel, gaNav: 'dot' });
      dot.addEventListener('click', () => {
        if (moving) settle();
        let delta = wrap(i - index);
        if (delta > count / 2) delta -= count; // 近い向きに送る
        if (delta) move(delta, 'dot', dot);
      });
      return dot;
    });
    dotsBox.replaceChildren(...dots);

    prev.addEventListener('click', () => move(-visible, 'prev', prev));
    next.addEventListener('click', () => move(visible, 'next', next));
    strip.addEventListener('transitionend', (event) => {
      if (event.target === strip && event.propertyName === 'transform' && moving) settle();
    });

    // 見切れたカードの上（CSS の ::before / ::after）を押したら、そちらへ送る
    viewport.addEventListener('click', (event) => {
      if (!looping() || event.target !== viewport) return;
      const box = viewport.getBoundingClientRect();
      const peek = parseFloat(root.style.getPropertyValue('--tyt-peek-w')) || 0;
      if (event.clientX <= box.left + peek) move(-visible, 'peek');
      else if (event.clientX >= box.right - peek) move(visible, 'peek');
    });

    // スワイプ・フリック（指・ペン・マウスのドラッグ。2026-09-30 に PC のマウスも追加）。縦のスクロールは妨げない。
    // 距離が短くても、離す直前の速さが速ければ（フリック）送る
    let drag = null;
    let suppressClickUntil = 0; // ドラッグを離したときに出るクリック1回だけを止める（次に押し始めたら解除）
    const FLICK_SPEED = 0.35; // px/ms（離す直前 100ms の平均）
    viewport.addEventListener('pointerdown', (event) => {
      if (!looping() || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      suppressClickUntil = 0;
      if (moving) settle();
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, active: false, samples: [[event.timeStamp, event.clientX]] };
    });
    viewport.addEventListener('dragstart', (event) => event.preventDefault()); // リンク・画像をつかんで運ぶ動きを止める
    viewport.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      if (event.pointerType === 'mouse' && !(event.buttons & 1)) { release(event); return; }
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      drag.samples.push([event.timeStamp, event.clientX]);
      while (drag.samples.length > 2 && event.timeStamp - drag.samples[0][0] > 100) drag.samples.shift();
      if (!drag.active) {
        if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
        if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
        drag.active = true;
        stopAll();
        root.classList.add('is-dragging');
        window.getSelection?.()?.removeAllRanges();
        try { viewport.setPointerCapture(event.pointerId); } catch (error) { /* 取れなくても続ける */ }
      }
      drag.dx = dx;
      place(false, dx);
    });
    const release = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const { active, dx, samples } = drag;
      drag = null;
      if (!active) return;
      root.classList.remove('is-dragging');
      suppressClickUntil = performance.now() + 400;
      const [[t0, x0]] = samples;
      const [t1, x1] = samples[samples.length - 1];
      const speed = t1 > t0 ? (x1 - x0) / (t1 - t0) : 0;
      const flick = Math.abs(speed) > FLICK_SPEED && Math.abs(dx) > 12 && Math.sign(speed) === Math.sign(dx);
      if (event.type !== 'pointercancel' && (flick || Math.abs(dx) > Math.min(60, stride * 0.18))) {
        move(dx < 0 ? visible : -visible, event.pointerType === 'mouse' ? 'drag' : 'swipe');
      } else goTo(index); // 戻す
    };
    viewport.addEventListener('pointerup', release);
    viewport.addEventListener('pointercancel', release);
    root.addEventListener('click', (event) => {
      if (performance.now() < suppressClickUntil) {
        suppressClickUntil = 0;
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    // トラックパッドの横スクロール。1回のなで（慣性を含む）で1回だけ送る
    let wheelSum = 0;
    let wheelAt = 0;
    let wheelDone = false;
    viewport.addEventListener('wheel', (event) => {
      if (!looping() || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      const now = performance.now();
      if (now - wheelAt > 250) { wheelSum = 0; wheelDone = false; }
      wheelAt = now;
      if (wheelDone) return;
      wheelSum += event.deltaX;
      if (Math.abs(wheelSum) > 40) {
        wheelDone = true;
        move(wheelSum > 0 ? visible : -visible, 'wheel');
      }
    }, { passive: false });

    root.addEventListener('keydown', (event) => {
      if (!looping() || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
      if (!event.target.closest('.tyt-arrow, .tyt-dot')) return; // カードの上だと、送った先でフォーカスが消えるため
      event.preventDefault();
      move(event.key === 'ArrowRight' ? visible : -visible, 'key');
    });

    let width = 0;
    const onResize = () => {
      const now = viewport.clientWidth;
      if (Math.abs(now - width) < 1) return;
      width = now;
      if (moving) settle();
      if (cssVisible() !== visible) build();
      else { measure(); place(false); }
    };
    build();
    width = viewport.clientWidth;
    if ('ResizeObserver' in window) new ResizeObserver(onResize).observe(viewport);
    else window.addEventListener('resize', onResize);
  };

  // ---------- 押したとき ----------
  document.addEventListener('click', (event) => {
    const root = event.target.closest?.('[data-tyt]');
    if (!root) return;
    const link = event.target.closest('[data-tyt-play]');
    if (link) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        track('yt_carousel_play', link, { play_mode: 'youtube' });
        return;
      }
      if (play(link)) event.preventDefault();
      else track('yt_carousel_play', link, { play_mode: 'youtube' });
      return;
    }
    const target = event.target.closest('[data-ga-event="yt_carousel_article_click"], [data-ga-event="yt_carousel_channel_click"]');
    if (target) track(target.dataset.gaEvent, target, { link_url: target.href });
  });

  const boot = () => document.querySelectorAll('[data-tyt]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
